import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { Navbar } from "@/components/site/Navbar";
import { Envelope, House, ChartLine, SignOut, Archive, Eye, Trash, ArrowSquareOut, CaretRight, Plus, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PropertyEditor } from "@/components/admin/PropertyEditor";

export default function AdminDashboardPage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("inquiries");
  const [inquiries, setInquiries] = useState([]);
  const [properties, setProperties] = useState([]);
  const [busy, setBusy] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingProperty, setEditingProperty] = useState(null);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) {
      navigate("/admin/login", { replace: true });
    }
  }, [user, loading, navigate]);

  const loadAll = useCallback(async () => {
    setBusy(true);
    try {
      const [i, p] = await Promise.all([api.get("/inquiries"), api.get("/properties")]);
      setInquiries(i.data);
      setProperties(p.data);
    } catch (e) {
      // ignore
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { if (user?.is_admin) loadAll(); }, [user, loadAll]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/inquiries/${id}`, { status });
      setInquiries((all) => all.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Could not update inquiry");
    }
  };

  const removeInquiry = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await api.delete(`/inquiries/${id}`);
      setInquiries((all) => all.filter((x) => x.id !== id));
      toast.success("Inquiry deleted");
    } catch {
      toast.error("Could not delete");
    }
  };

  const togglePropertyAvailable = async (slug, available) => {
    try {
      const { data } = await api.patch(`/properties/${slug}`, { available });
      setProperties((all) => all.map((p) => (p.slug === slug ? data : p)));
      toast.success(`Marked ${available ? "available" : "let agreed"}`);
    } catch {
      toast.error("Could not update");
    }
  };

  const stats = {
    inquiries: inquiries.length,
    new: inquiries.filter((i) => i.status === "new").length,
    properties: properties.length,
    available: properties.filter((p) => p.available).length,
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center text-[var(--text-tertiary)]">
        Loading…
      </div>
    );
  }

  return (
    <div className="App">
      <Navbar />

      <div className="grid grid-cols-12 max-w-7xl mx-auto px-6 md:px-12 py-10 gap-8" data-testid="admin-dashboard">
        {/* Sidebar */}
        <aside className="col-span-12 lg:col-span-3">
          <div className="bg-[var(--bg-secondary)] border border-[var(--border-subtle)] p-6">
            <div className="label-uppercase">Signed in</div>
            <div className="mt-2 font-display text-xl truncate" data-testid="admin-user-email">{user.email}</div>
            <div className="text-xs text-[var(--text-tertiary)] mt-1">Owner / Admin</div>

            <nav className="mt-6 space-y-1">
              <SidebarItem active={tab === "overview"} onClick={() => setTab("overview")} icon={<ChartLine size={18} />} label="Overview" testId="tab-overview" />
              <SidebarItem active={tab === "inquiries"} onClick={() => setTab("inquiries")} icon={<Envelope size={18} />} label={`Inquiries (${stats.new})`} testId="tab-inquiries" />
              <SidebarItem active={tab === "properties"} onClick={() => setTab("properties")} icon={<House size={18} />} label="Properties" testId="tab-properties" />
            </nav>

            <button
              onClick={async () => { await logout(); navigate("/"); }}
              className="btn-outline w-full mt-8 text-sm justify-center"
              data-testid="admin-logout-button"
            >
              <SignOut size={16} /> Sign out
            </button>
          </div>
        </aside>

        <main className="col-span-12 lg:col-span-9">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
            <StatBox label="Inquiries" value={stats.inquiries} />
            <StatBox label="New" value={stats.new} accent />
            <StatBox label="Properties" value={stats.properties} />
            <StatBox label="Available" value={stats.available} />
          </div>

          {tab === "overview" && (
            <div className="border border-[var(--border-subtle)] p-8" data-testid="overview-pane">
              <div className="label-uppercase">Welcome back</div>
              <h2 className="h2 text-3xl md:text-4xl mt-3">Hi {user.name?.split(' ')[0] || 'there'} 👋</h2>
              <p className="mt-4 text-[var(--text-secondary)] max-w-xl">
                You have {stats.new} new inquiry{stats.new === 1 ? "" : "s"} and {stats.available} of {stats.properties} properties currently marked available.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button onClick={() => setTab("inquiries")} className="btn-primary">Review inquiries <CaretRight size={16} weight="bold" /></button>
                <button onClick={() => setTab("properties")} className="btn-outline">Manage properties</button>
              </div>
            </div>
          )}

          {tab === "inquiries" && (
            <div className="border border-[var(--border-subtle)]" data-testid="admin-inquiries-pane">
              <div className="flex items-center justify-between border-b border-[var(--border-subtle)] p-6">
                <h2 className="font-display text-2xl">Inquiries</h2>
                <span className="label-uppercase">{inquiries.length} total</span>
              </div>
              {busy ? (
                <div className="p-10 text-center text-[var(--text-tertiary)]">Loading…</div>
              ) : inquiries.length === 0 ? (
                <div className="p-10 text-center text-[var(--text-tertiary)]">No inquiries yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="admin-inquiries-table">
                    <thead>
                      <tr className="text-left border-b border-[var(--border-subtle)] bg-[var(--bg-secondary)]">
                        <Th>Date</Th><Th>Name</Th><Th>Email</Th><Th>Property</Th><Th>Status</Th><Th className="text-right">Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {inquiries.map((i) => (
                        <tr key={i.id} className="border-b border-[var(--border-subtle)] hover:bg-[var(--bg-secondary)]/50" data-testid={`inquiry-row-${i.id}`}>
                          <Td>{new Date(i.created_at).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</Td>
                          <Td className="font-medium">{i.name}</Td>
                          <Td><a href={`mailto:${i.email}`} className="link-inline">{i.email}</a></Td>
                          <Td>{i.property_name || "—"}</Td>
                          <Td>
                            <StatusPill status={i.status} />
                          </Td>
                          <Td className="text-right">
                            <div className="inline-flex gap-1">
                              {i.status !== "viewed" && (
                                <button onClick={() => updateStatus(i.id, "viewed")} className="p-2 hover:bg-[var(--bg-tertiary)]" title="Mark viewed" data-testid={`inquiry-view-${i.id}`}><Eye size={16} /></button>
                              )}
                              {i.status !== "archived" && (
                                <button onClick={() => updateStatus(i.id, "archived")} className="p-2 hover:bg-[var(--bg-tertiary)]" title="Archive" data-testid={`inquiry-archive-${i.id}`}><Archive size={16} /></button>
                              )}
                              <button onClick={() => removeInquiry(i.id)} className="p-2 hover:bg-red-50 text-red-600" title="Delete" data-testid={`inquiry-delete-${i.id}`}><Trash size={16} /></button>
                            </div>
                          </Td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {tab === "properties" && (
            <div data-testid="admin-properties-pane">
              <div className="flex items-center justify-between mb-6">
                <h2 className="font-display text-2xl">Properties</h2>
                <button
                  onClick={() => { setEditingProperty(null); setEditorOpen(true); }}
                  className="btn-primary text-sm"
                  data-testid="add-property-button"
                >
                  <Plus size={16} weight="bold" /> Add property
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {properties.map((p) => (
                  <div key={p.id} className="border border-[var(--border-subtle)] bg-white" data-testid={`admin-property-card-${p.slug}`}>
                    <div className="aspect-[16/9] overflow-hidden bg-[var(--bg-secondary)]">
                      {p.coming_soon ? (
                        <div className="w-full h-full flex items-center justify-center bg-[var(--brand)] text-white">
                          <div className="text-center">
                            <div className="label-uppercase !text-[var(--accent)]">Coming Soon</div>
                            <div className="font-display text-2xl mt-1">{p.name}</div>
                          </div>
                        </div>
                      ) : (
                        <img src={p.hero_image} alt={p.name} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="p-5">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="h3 text-xl">{p.name}</h3>
                          <div className="text-xs text-[var(--text-tertiary)] mt-1">{p.address || "—"}</div>
                        </div>
                        <Link to={`/properties/${p.slug}`} target="_blank" className="p-2 hover:bg-[var(--bg-secondary)]" title="View live">
                          <ArrowSquareOut size={16} />
                        </Link>
                      </div>
                      <div className="mt-4 flex items-center justify-between">
                        <div className="font-display text-lg">
                          {p.coming_soon ? (
                            <span className="text-[var(--text-tertiary)]">TBC</span>
                          ) : p.price_pppw ? (
                            <>£{p.price_pppw}<span className="text-xs text-[var(--text-tertiary)] ml-1">pppw</span></>
                          ) : (
                            <>£{p.price_pcm?.toLocaleString()}<span className="text-xs text-[var(--text-tertiary)] ml-1">pcm</span></>
                          )}
                        </div>
                        <StatusPill status={p.coming_soon ? "coming" : p.available ? "available" : "let"} />
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-4">
                        <button
                          onClick={() => { setEditingProperty(p); setEditorOpen(true); }}
                          className="btn-outline justify-center text-sm"
                          data-testid={`property-edit-${p.slug}`}
                        >
                          <PencilSimple size={16} /> Edit
                        </button>
                        <button
                          onClick={() => togglePropertyAvailable(p.slug, !p.available)}
                          className="btn-outline justify-center text-sm"
                          data-testid={`property-toggle-${p.slug}`}
                        >
                          {p.available ? "Mark Let" : "Mark Available"}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Empty state add card */}
                <button
                  onClick={() => { setEditingProperty(null); setEditorOpen(true); }}
                  className="min-h-[300px] border-2 border-dashed border-[var(--border-subtle)] hover:border-[var(--brand)] hover:bg-[var(--bg-secondary)] transition-colors flex flex-col items-center justify-center text-[var(--text-tertiary)] hover:text-[var(--brand)]"
                  data-testid="add-property-card"
                >
                  <Plus size={28} />
                  <span className="mt-2 font-medium">Add new property</span>
                </button>
              </div>
            </div>
          )}
        </main>
      </div>

      <PropertyEditor
        open={editorOpen}
        onOpenChange={setEditorOpen}
        property={editingProperty}
        onSaved={(saved) => {
          setProperties((all) => {
            const exists = all.find((x) => x.slug === saved.slug);
            return exists
              ? all.map((x) => (x.slug === saved.slug ? saved : x))
              : [...all, saved];
          });
        }}
        onDeleted={(slug) => setProperties((all) => all.filter((x) => x.slug !== slug))}
      />
    </div>
  );
}

const SidebarItem = ({ active, onClick, icon, label, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 text-sm transition-colors ${
      active ? "bg-[var(--text-primary)] text-white" : "hover:bg-white text-[var(--text-secondary)]"
    }`}
  >
    {icon} <span>{label}</span>
  </button>
);

const StatBox = ({ label, value, accent }) => (
  <div className={`border border-[var(--border-subtle)] p-5 ${accent ? "bg-[var(--text-primary)] text-white" : "bg-white"}`}>
    <div className={`label-uppercase ${accent ? "!text-white/60" : ""}`}>{label}</div>
    <div className="font-display text-4xl mt-2 tracking-tighter">{value}</div>
  </div>
);

const Th = ({ children, className = "" }) => (
  <th className={`px-4 py-3 label-uppercase ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-4 py-3 align-middle ${className}`}>{children}</td>
);

const StatusPill = ({ status }) => {
  const map = {
    new: { c: "bg-[var(--brand)] text-white", t: "New" },
    viewed: { c: "bg-[var(--bg-tertiary)] text-[var(--text-primary)]", t: "Viewed" },
    archived: { c: "bg-[var(--bg-secondary)] text-[var(--text-tertiary)]", t: "Archived" },
    available: { c: "bg-[var(--status-available)] text-white", t: "Available" },
    let: { c: "bg-[var(--status-let)] text-white", t: "Let Agreed" },
    coming: { c: "bg-[var(--accent)] text-[var(--brand)]", t: "Coming Soon" },
  };
  const s = map[status] || map.new;
  return <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-[0.15em] ${s.c}`}>{s.t}</span>;
};
